import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic,
  MicOff,
  Square,
  Trash2,
  Settings,
  Activity,
  Clock,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';
import Sanscript from 'sanscript';
import { getApiEndpoint, apiRequest } from '../config/api';

// Performance optimization: Create a shared AbortController for request cancellation
let sharedAbortController = null;

// Performance optimization: Connection pooling simulation with request caching
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Performance optimization: Optimized audio constraints for better performance
const OPTIMIZED_AUDIO_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: false, // Disabled for better performance as per server optimization
    sampleRate: 16000,
    channelCount: 1,
    latency: 0.01, // 10ms low-latency audio capture
  }
};

// Custom hooks for the LiveTranscribe functionality
const useLiveTranscription = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [partialText, setPartialText] = useState('');
  const [audioProcessedMs, setAudioProcessedMs] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionTime, setSessionTime] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const recordTranscribeRef = useRef(null);
  const sessionTimerRef = useRef(null);

  // Settings state
  const [settings, setSettings] = useState({
    enableSpeakerDiarization: false,
    enableLanguageIdentification: false,
    enableEndpointDetection: true,
    enableHindiTransliteration: false
  });

  // Update word count when final text changes
  useEffect(() => {
    const words = finalText.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [finalText]);

  // Session timer effect
  useEffect(() => {
    if (isRecording && sessionStartTime) {
      sessionTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
        setSessionTime(elapsed);
      }, 1000);
    } else {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    }

    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, [isRecording, sessionStartTime]);

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const showError = useCallback((message) => {
    setError(message);
    setSuccess('');
  }, []);

  const showSuccess = useCallback((message) => {
    setSuccess(message);
    setError('');
  }, []);

  // Performance optimization: Cached API key fetching with connection pooling
  const fetchApiKeyOptimized = useCallback(async () => {
    const cacheKey = 'soniox_temp_key';
    const now = Date.now();

    // Check cache first for performance
    if (requestCache.has(cacheKey)) {
      const cached = requestCache.get(cacheKey);
      if (now - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }
      requestCache.delete(cacheKey);
    }

    // Cancel any previous request
    if (sharedAbortController) {
      sharedAbortController.abort();
    }
    sharedAbortController = new AbortController();

    const startTime = performance.now();
    const apiUrl = getApiEndpoint('SONIOX_TEMP_KEY');

    // Optimized request with timeout and abort signal
    const response = await apiRequest(apiUrl, {
      method: 'POST',
      signal: sharedAbortController.signal,
      // Performance optimization: Reduced timeout as per server optimization
      timeout: 5000 // 5 seconds instead of default 10
    });

    const duration = performance.now() - startTime;

    // Performance monitoring: Reduced logging as per server optimization
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Key Request: ${response.status} (${duration.toFixed(1)}ms)`);
    }

    if (!response.ok) {
      throw new Error(`Failed to get API key: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`API Key Error: ${data.error}`);
    }

    const apiKey = data.api_key;
    if (!apiKey) {
      throw new Error('No API key received from server');
    }

    // Cache the result for performance
    requestCache.set(cacheKey, {
      data: apiKey,
      timestamp: now
    });

    return apiKey;
  }, []);

  // Core transcription functions with performance optimizations
  const startRecording = useCallback(async () => {
    try {
      setIsLoading(true);
      clearMessages();
      showSuccess('Initializing...');

      // Performance optimization: Use optimized API key fetching
      const apiKey = await fetchApiKeyOptimized();

      // Performance optimization: Reduced logging
      if (process.env.NODE_ENV === 'development') {
        console.log('API key received, length:', apiKey.length);
      }

      // Dynamic import of Soniox library
      const { RecordTranscribe } = await import('@soniox/speech-to-text-web');
      recordTranscribeRef.current = new RecordTranscribe({
        apiKey,
        // Performance optimization: Add audio constraints for better performance
        audioConstraints: OPTIMIZED_AUDIO_CONSTRAINTS.audio
      });

      // Performance optimization: Start transcription with optimized settings
      await recordTranscribeRef.current.start({
        model: "stt-rt-preview-v2",
        language_hints: ["en", "es", "hi"],
        enable_speaker_diarization: settings.enableSpeakerDiarization,
        enable_language_identification: settings.enableLanguageIdentification,
        enable_endpoint_detection: settings.enableEndpointDetection,
        enable_hindi_transliteration: settings.enableHindiTransliteration,
        // Performance optimization: Optimized chunk settings (increased from 100ms to 250ms)
        chunk_length_ms: 250, // Reduced network overhead as per server optimization
        onStarted: () => {
          // Performance optimization: Reduced logging
          if (process.env.NODE_ENV === 'development') {
            console.log('Recording started successfully');
          }
          setIsRecording(true);
          setSessionStartTime(Date.now());
          clearMessages();
        },
        onPartialResult: (result) => {
          // Performance optimization: Reduced logging frequency
          if (process.env.NODE_ENV === 'development' && result.tokens?.length > 0) {
            console.log('Transcription result received');
          }

          // Update audio processing time
          if (result.total_audio_proc_ms) {
            setAudioProcessedMs(result.total_audio_proc_ms);
          }

          // Process tokens with performance optimization
          if (result.tokens && result.tokens.length > 0) {
            let newFinalText = '';
            let newPartialText = '';

            // Performance optimization: Use for...of loop for better performance
            for (const token of result.tokens) {
              let tokenText = token.text;

              // Apply Hindi transliteration if enabled
              if (settings.enableHindiTransliteration && token.language === 'hi') {
                try {
                  tokenText = Sanscript.t(tokenText, 'devanagari', 'itrans');
                } catch (error) {
                  // Performance optimization: Reduced error logging
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('Hindi transliteration failed:', error);
                  }
                  // Fall back to original text if transliteration fails
                }
              }

              if (token.is_final) {
                newFinalText += tokenText;
              } else {
                newPartialText += tokenText;
              }
            }

            // Performance optimization: Batch state updates
            if (newFinalText || newPartialText !== partialText) {
              if (newFinalText) {
                setFinalText(prev => prev + newFinalText);
              }
              setPartialText(newPartialText);
            }
          }
        },
        onFinished: () => {
          setIsRecording(false);
          // Performance optimization: Clear abort controller
          if (sharedAbortController) {
            sharedAbortController = null;
          }
        },
        onError: (status, message) => {
          showError(`Transcription error: ${status} - ${message}`);
          setIsRecording(false);
          // Performance optimization: Clear abort controller on error
          if (sharedAbortController) {
            sharedAbortController = null;
          }
        }
      });

    } catch (error) {
      // Performance optimization: Enhanced error handling with cleanup
      if (error.name === 'AbortError') {
        showError('Request was cancelled');
      } else {
        showError(`Failed to start recording: ${error.message}`);
      }
      setIsRecording(false);

      // Performance optimization: Cleanup on error
      if (sharedAbortController) {
        sharedAbortController = null;
      }
    } finally {
      setIsLoading(false);
    }
  }, [settings, clearMessages, showError, showSuccess, fetchApiKeyOptimized, partialText]);

  const stopRecording = useCallback(async () => {
    if (recordTranscribeRef.current && isRecording) {
      try {
        await recordTranscribeRef.current.stop();
      } catch (error) {
        // Performance optimization: Silent error handling for stop operation
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error stopping recording:', error);
        }
      } finally {
        // Performance optimization: Cleanup abort controller
        if (sharedAbortController) {
          sharedAbortController = null;
        }
      }
    }
  }, [isRecording]);

  const clearTranscription = useCallback(() => {
    setFinalText('');
    setPartialText('');
    setAudioProcessedMs(0);
    setSessionTime(0);

    // Performance optimization: Clear cache when clearing transcription
    requestCache.clear();
  }, []);

  return {
    // State
    isRecording,
    finalText,
    partialText,
    audioProcessedMs,
    sessionTime,
    wordCount,
    error,
    success,
    isLoading,
    settings,

    // Setters
    setSettings,

    // Refs
    recordTranscribeRef,

    // Functions
    startRecording,
    stopRecording,
    clearTranscription,
    clearMessages,
    showError,
    showSuccess
  };
};

// Browser compatibility check hook
const useBrowserCompatibility = () => {
  const [isSupported, setIsSupported] = useState(true);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    const checkCompatibility = () => {
      const detectedIssues = [];
      
      if (!navigator.mediaDevices?.getUserMedia) {
        detectedIssues.push('Microphone access not supported');
      }
      
      if (!window.AudioContext && !window.webkitAudioContext) {
        detectedIssues.push('Audio processing not supported');
      }
      
      if (!window.WebSocket) {
        detectedIssues.push('WebSocket not supported');
      }

      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        detectedIssues.push('HTTPS required for microphone access');
      }
      
      setIssues(detectedIssues);
      setIsSupported(detectedIssues.length === 0);
    };

    checkCompatibility();
  }, []);

  return { isSupported, issues };
};

// Main LiveTranscribe component
const LiveTranscribe = () => {
  const {
    isRecording,
    finalText,
    partialText,
    audioProcessedMs,
    sessionTime,
    wordCount,
    error,
    success,
    isLoading,
    settings,
    setSettings,
    recordTranscribeRef,
    startRecording,
    stopRecording,
    clearTranscription,
    clearMessages,
    showError,
    showSuccess
  } = useLiveTranscription();

  const { isSupported, issues } = useBrowserCompatibility();

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Format audio processing time
  const formatAudioTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    return formatTime(seconds);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-blue-800 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl mb-4">
              <Mic className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Live Transcription
            </h1>
            <p className="text-xl text-blue-100">
              Real-time speech-to-text powered by Soniox AI
            </p>
          </div>

          {/* Browser compatibility warning */}
          {!isSupported && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mb-6 backdrop-blur-sm">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                <span className="text-red-300 font-medium">Browser Compatibility Issues:</span>
              </div>
              <ul className="mt-2 text-red-200 list-disc list-inside">
                {issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Status Messages */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mb-6 backdrop-blur-sm">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                <span className="text-red-200">{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4 mb-6 backdrop-blur-sm">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                <span className="text-green-200">{success}</span>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Transcription Area - Takes up 2 columns on large screens */}
            <div className="lg:col-span-2">
              <div className={`bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border-2 transition-all duration-300 ${
                isRecording ? 'border-blue-400 shadow-blue-500/20' : 'border-white/20'
              }`}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Transcription</h3>
                    <div className={`flex items-center space-x-2 ${
                      isRecording ? 'text-green-400' : 'text-gray-300'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${
                        isRecording ? 'bg-green-400 animate-pulse' : 'bg-gray-400'
                      }`} />
                      <span className="text-sm font-medium">
                        {isRecording ? 'Recording' : 'Stopped'}
                      </span>
                    </div>
                  </div>

                  <div className="min-h-[300px] p-4 bg-black/20 rounded-xl border border-white/10">
                    {!finalText && !partialText && !isRecording && (
                      <div className="flex items-center justify-center h-full text-gray-300">
                        <div className="text-center">
                          <Mic className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                          <p>Click "Start Recording" to begin live transcription...</p>
                        </div>
                      </div>
                    )}

                    {isRecording && !finalText && !partialText && (
                      <div className="flex items-center justify-center h-full text-blue-300">
                        <div className="text-center">
                          <Activity className="w-12 h-12 mx-auto mb-3 animate-pulse" />
                          <p>Listening... Start speaking!</p>
                        </div>
                      </div>
                    )}

                    {(finalText || partialText) && (
                      <div className="text-lg leading-relaxed">
                        <span className="text-white font-medium">{finalText}</span>
                        <span className="text-blue-200 italic">{partialText}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Control Panel - Takes up 1 column */}
            <div className="space-y-6">
              {/* Recording Controls */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Controls</h3>
                <div className="space-y-3">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={!isSupported || isLoading}
                    className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                      isRecording
                        ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/25'
                        : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-500 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25'
                    }`}
                  >
                    {isLoading ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : isRecording ? (
                      <Square className="w-5 h-5" />
                    ) : (
                      <Mic className="w-5 h-5" />
                    )}
                    <span>
                      {isLoading ? 'Initializing...' : isRecording ? 'Stop Recording' : 'Start Recording'}
                    </span>
                  </button>

                  <button
                    onClick={clearTranscription}
                    className="w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-xl font-medium bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all duration-300"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>Clear Text</span>
                  </button>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Statistics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-100">Session Time</span>
                    </div>
                    <span className="font-semibold text-white">{formatTime(sessionTime)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-green-400" />
                      <span className="text-blue-100">Audio Processed</span>
                    </div>
                    <span className="font-semibold text-white">{formatAudioTime(audioProcessedMs)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4 text-purple-400" />
                      <span className="text-blue-100">Words</span>
                    </div>
                    <span className="font-semibold text-white">{wordCount}</span>
                  </div>
                </div>
              </div>

              {/* Settings Panel */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Settings
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Speaker Diarization</label>
                      <p className="text-xs text-blue-200">Identify different speakers</p>
                    </div>
                    <button
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        enableSpeakerDiarization: !prev.enableSpeakerDiarization
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enableSpeakerDiarization ? 'bg-blue-500' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enableSpeakerDiarization ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Language Detection</label>
                      <p className="text-xs text-blue-200">Auto-detect spoken language</p>
                    </div>
                    <button
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        enableLanguageIdentification: !prev.enableLanguageIdentification
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enableLanguageIdentification ? 'bg-blue-500' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enableLanguageIdentification ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Endpoint Detection</label>
                      <p className="text-xs text-blue-200">Detect speech boundaries</p>
                    </div>
                    <button
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        enableEndpointDetection: !prev.enableEndpointDetection
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enableEndpointDetection ? 'bg-blue-500' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enableEndpointDetection ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Hindi Transliteration</label>
                      <p className="text-xs text-blue-200">Convert Hindi to Latin script</p>
                    </div>
                    <button
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        enableHindiTransliteration: !prev.enableHindiTransliteration
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enableHindiTransliteration ? 'bg-blue-500' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enableHindiTransliteration ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTranscribe;
